import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Instagram, Twitter, Share2, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutPage = () => {
    const { cart, clearCart } = useCart();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [orderNumber, setOrderNumber] = useState('');
    const [copied, setCopied] = useState(false);
    
    const [formData, setFormData] = useState({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        shipping_address: '',
        city: '',
        pincode: ''
    });

    const subtotal = cart.total;
    const shipping = subtotal >= 999 ? 0 : 99;
    const total = subtotal + shipping;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate
        if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || 
            !formData.shipping_address || !formData.city || !formData.pincode) {
            toast.error('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            const orderItems = cart.items.map(item => ({
                product_id: item.product_id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                size: item.size
            }));

            const response = await axios.post(`${API}/orders`, {
                ...formData,
                items: orderItems
            });

            setOrderNumber(response.data.order_number);
            setOrderPlaced(true);
            await clearCart();
            toast.success('Order placed successfully!');
        } catch (error) {
            console.error('Error placing order:', error);
            toast.error('Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    const shareText = `Just ordered some fresh streetwear from @YESSuhchill! 🔥 Order: ${orderNumber} #YESSuhchill #Streetwear #ChillVibes`;
    const shareUrl = 'https://yessuhchill.com';

    const handleShareInstagram = () => {
        // Instagram doesn't have direct share API, open Instagram with copied text
        navigator.clipboard.writeText(shareText);
        toast.success('Caption copied! Opening Instagram...');
        window.open('https://www.instagram.com/', '_blank');
    };

    const handleShareTwitter = () => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank');
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        setCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    if (orderPlaced) {
        return (
            <Layout>
                <div className="pt-32 md:pt-40 pb-16 min-h-screen" data-testid="order-success">
                    <div className="max-w-xl mx-auto px-4 md:px-8 text-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', duration: 0.5 }}
                        >
                            <CheckCircle className="w-24 h-24 mx-auto mb-6 text-lime" />
                        </motion.div>
                        <h1 className="font-heading text-4xl md:text-6xl mb-4">ORDER CONFIRMED!</h1>
                        <p className="text-muted-foreground mb-2">Thank you for your order.</p>
                        <p className="text-lg mb-8">
                            Order Number: <span className="text-lime font-bold" data-testid="order-number">{orderNumber}</span>
                        </p>
                        <p className="text-sm text-muted-foreground mb-8">
                            We'll send you an email with tracking details once your order ships.
                        </p>

                        {/* Share Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="bg-card border border-border p-6 mb-8"
                        >
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <Share2 className="w-5 h-5 text-lime" />
                                <span className="font-heading text-xl">SHARE YOUR ORDER</span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-6">
                                Show off your new streetwear! Share with the squad 🔥
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                <button
                                    onClick={handleShareInstagram}
                                    className="flex items-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-5 py-3 text-xs font-bold tracking-wider uppercase hover:opacity-90 transition-opacity"
                                    data-testid="share-instagram"
                                >
                                    <Instagram className="w-4 h-4" />
                                    INSTAGRAM
                                </button>
                                <button
                                    onClick={handleShareTwitter}
                                    className="flex items-center gap-2 bg-black border border-white/20 text-white px-5 py-3 text-xs font-bold tracking-wider uppercase hover:border-lime hover:text-lime transition-colors"
                                    data-testid="share-twitter"
                                >
                                    <Twitter className="w-4 h-4" />
                                    TWITTER / X
                                </button>
                                <button
                                    onClick={handleCopyLink}
                                    className="flex items-center gap-2 border border-border px-5 py-3 text-xs font-bold tracking-wider uppercase hover:border-lime hover:text-lime transition-colors"
                                    data-testid="share-copy"
                                >
                                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {copied ? 'COPIED!' : 'COPY'}
                                </button>
                            </div>
                        </motion.div>

                        <button
                            onClick={() => navigate('/products')}
                            className="bg-lime text-black px-8 py-4 text-xs font-bold tracking-[0.14em] uppercase hover:tracking-[0.2em] transition-all"
                            data-testid="continue-shopping"
                        >
                            CONTINUE SHOPPING
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    if (cart.items.length === 0) {
        navigate('/cart');
        return null;
    }

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16" data-testid="checkout-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <button
                        onClick={() => navigate('/cart')}
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-lime mb-8 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        BACK TO CART
                    </button>

                    <h1 className="font-heading text-5xl md:text-7xl mb-8 md:mb-12">CHECKOUT</h1>

                    <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Form */}
                        <div className="lg:col-span-2">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="bg-card border border-border p-6">
                                    <h2 className="font-heading text-2xl mb-6">CONTACT INFORMATION</h2>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                                FULL NAME *
                                            </label>
                                            <input
                                                type="text"
                                                name="customer_name"
                                                value={formData.customer_name}
                                                onChange={handleChange}
                                                className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                                placeholder="John Doe"
                                                data-testid="input-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                                EMAIL *
                                            </label>
                                            <input
                                                type="email"
                                                name="customer_email"
                                                value={formData.customer_email}
                                                onChange={handleChange}
                                                className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                                placeholder="john@example.com"
                                                data-testid="input-email"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                                PHONE NUMBER *
                                            </label>
                                            <input
                                                type="tel"
                                                name="customer_phone"
                                                value={formData.customer_phone}
                                                onChange={handleChange}
                                                className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                                placeholder="+91 9876543210"
                                                data-testid="input-phone"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-card border border-border p-6">
                                    <h2 className="font-heading text-2xl mb-6">SHIPPING ADDRESS</h2>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                                ADDRESS *
                                            </label>
                                            <input
                                                type="text"
                                                name="shipping_address"
                                                value={formData.shipping_address}
                                                onChange={handleChange}
                                                className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                                placeholder="123 Street Name, Apartment 4B"
                                                data-testid="input-address"
                                            />
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                                    CITY *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                                    placeholder="Mumbai"
                                                    data-testid="input-city"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                                    PINCODE *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="pincode"
                                                    value={formData.pincode}
                                                    onChange={handleChange}
                                                    className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                                    placeholder="400001"
                                                    data-testid="input-pincode"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-card border border-border p-6">
                                    <h2 className="font-heading text-2xl mb-4">PAYMENT</h2>
                                    <p className="text-muted-foreground text-sm mb-4">
                                        This is a demo checkout. No actual payment will be processed.
                                    </p>
                                    <div className="bg-lime/10 border border-lime/30 p-4">
                                        <span className="text-lime text-sm font-medium">Cash on Delivery</span>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Pay when your order arrives at your doorstep.
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-lime text-black py-4 text-sm font-bold tracking-[0.14em] uppercase hover:tracking-[0.2em] transition-all duration-300 disabled:opacity-50"
                                    data-testid="place-order-btn"
                                >
                                    {loading ? 'PROCESSING...' : `PLACE ORDER — ₹${total.toLocaleString()}`}
                                </button>
                            </form>
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="bg-card border border-border p-6 sticky top-32">
                                <h2 className="font-heading text-2xl mb-6">ORDER SUMMARY</h2>
                                
                                <div className="space-y-4 pb-6 border-b border-border max-h-60 overflow-y-auto">
                                    {cart.items.map((item) => (
                                        <div key={`${item.product_id}-${item.size}`} className="flex gap-3">
                                            <div className="w-16 h-20 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                                                <span className="text-2xl opacity-50">👕</span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium leading-tight">{item.name}</p>
                                                {item.size && <p className="text-xs text-muted-foreground">Size: {item.size}</p>}
                                                <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                                            </div>
                                            <span className="text-sm font-medium">
                                                ₹{(item.price * item.quantity).toLocaleString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="space-y-3 py-6 border-b border-border">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Subtotal</span>
                                        <span>₹{subtotal.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Shipping</span>
                                        <span className={shipping === 0 ? 'text-lime' : ''}>
                                            {shipping === 0 ? 'FREE' : `₹${shipping}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex justify-between py-6">
                                    <span className="font-heading text-xl">TOTAL</span>
                                    <span className="font-heading text-xl">₹{total.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
